import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
    Box, 
    Button, 
    Typography, 
    Container, 
    CircularProgress, 
    Grid, 
    TextField,
    Snackbar,
    Alert,
    Card,
    CardContent,
    Chip,
    alpha
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { 
    ArrowBack as ArrowBackIcon,
    Visibility as VisibilityIcon,
    EmojiEvents as TrophyIcon,
    FavoriteBorder as ValuesIcon,
    TrendingUp as SuccessIcon,
    BusinessCenter as BusinessIcon,
    Star as StarIcon,
    Save as SaveIcon
} from '@mui/icons-material';
import { jwtDecode } from 'jwt-decode';
import api from '../services/api';

const formFields = [
  { 
    name: 'missao', 
    label: 'Missão', 
    helper: 'Qual é o propósito fundamental da sua organização?',
    icon: <TrophyIcon />, 
    color: '#3b82f6' 
  },
  { 
    name: 'visaoDeFuturo', 
    label: 'Visão de Futuro', 
    helper: 'Onde vocês aspiram chegar nos próximos anos?',
    icon: <VisibilityIcon />, 
    color: '#8b5cf6' 
  },
  { 
    name: 'valores', 
    label: 'Valores', 
    helper: 'Quais princípios guiam suas ações e decisões?',
    icon: <ValuesIcon />, 
    color: '#ec4899' 
  },
  { 
    name: 'fatoresCriticos', 
    label: 'Fatores Críticos de Sucesso', 
    helper: 'O que é essencial para alcançar a missão e a visão?',
    icon: <SuccessIcon />, 
    color: '#f59e0b' 
  },
  { 
    name: 'businessOverview', 
    label: 'Business Overview', 
    helper: 'Descreva seu negócio, mercado e principais operações.',
    icon: <BusinessIcon />, 
    color: '#14b8a6' 
  },
  { 
    name: 'valueProposition', 
    label: 'Proposta de Valor', 
    helper: 'O que torna sua oferta única e valiosa para os clientes?',
    icon: <StarIcon />, 
    color: '#22c55e' 
  },
];

const StrategyPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const token = localStorage.getItem('app_token');
    if (token) {
      try {
        const decodedUser = jwtDecode(token);
        setUser(decodedUser);
      } catch (e) {
        console.error("Token inválido:", e);
        navigate('/access-denied');
        return;
      }
    }

    const fetchElements = async () => {
      setIsLoading(true);
      try {
        const response = await api.get('/elements');
        if (response.data) {
            setFormData(response.data);
        }
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
        if (error.response && error.response.status === 401) {
          navigate('/access-denied');
        } else {
          setSnackbar({ open: true, message: 'Erro ao carregar dados da estratégia.', severity: 'error' });
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchElements();
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const response = await api.put('/elements', formData);
      setFormData(response.data);
      setSnackbar({ open: true, message: '✓ Estratégia salva com sucesso!', severity: 'success' });
    } catch (error) {
      console.error("Erro ao salvar dados:", error);
      if (error.response && error.response.status === 401) {
        navigate('/access-denied');
      } else {
        setSnackbar({ open: true, message: '✗ Falha ao salvar. Tente novamente.', severity: 'error' });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  const canEdit = user && user.role !== 'leitor';

  if (isLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        gap: 3,
        backgroundColor: '#f8fafc'
      }}>
        <CircularProgress size={64} thickness={4} sx={{ color: '#FF834F' }} />
        <Typography variant="h5" sx={{ color: '#64748b', fontWeight: 600 }}>
          A carregar estratégia...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: '#f8fafc',
      py: 4,
      px: 2
    }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button 
            component={Link} 
            to="/" 
            startIcon={<ArrowBackIcon />}
            sx={{ 
              mb: 3,
              color: '#FF834F',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: alpha('#FF834F', 0.1)
              }
            }}
          >
            Voltar para o Dashboard
          </Button>

          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 800, 
                color: '#1e293b',
                mb: 1
              }}
            >
              Business Awareness
            </Typography>
            <Typography 
              variant="h6"
              sx={{ 
                color: '#64748b',
                fontWeight: 400
              }}
            >
              Defina os pilares estratégicos que guiam a sua organização
            </Typography>
            {!canEdit && (
              <Chip 
                label="👁️ Modo Leitura" 
                sx={{ 
                  mt: 2,
                  backgroundColor: alpha('#FF834F', 0.1),
                  color: '#FF834F',
                  fontWeight: 700,
                  border: `2px solid ${alpha('#FF834F', 0.3)}`
                }}
              />
            )}
          </Box>
        </Box>

 {/* Form Section - Perfeitamente Centralizado */}
<Box component="form" onSubmit={handleSubmit} noValidate>
  <Grid 
    container 
    spacing={3}
    justifyContent="center" // ✅ Centraliza os items do grid
    sx={{ 
      maxWidth: '1400px',
      margin: '0 auto',
      px: 2 // ✅ Padding lateral para não encostar nas bordas
    }}
  >
    {formFields.map((field) => (
      <Grid item xs={12} md={4} key={field.name}>
        <Card 
          elevation={0}
          sx={{ 
            height: '340px',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '16px',
            backgroundColor: 'white',
            border: '2px solid #e2e8f0',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-6px)',
              boxShadow: `0 12px 24px ${alpha(field.color, 0.2)}`,
              borderColor: field.color
            }
          }}
        >
          <CardContent sx={{ 
            p: 3, 
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Card Header */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              mb: 2
            }}>
              <Box sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(135deg, ${field.color} 0%, ${alpha(field.color, 0.8)} 100%)`,
                color: 'white',
                fontSize: '1.5rem',
                flexShrink: 0,
                boxShadow: `0 4px 12px ${alpha(field.color, 0.3)}`
              }}>
                {field.icon}
              </Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700,
                  color: '#1e293b',
                  fontSize: '1.05rem',
                  lineHeight: 1.3
                }}
              >
                {field.label}
              </Typography>
            </Box>

            {/* Helper Text */}
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#64748b',
                mb: 2,
                lineHeight: 1.5,
                fontSize: '0.85rem',
                minHeight: '40px'
              }}
            >
              {field.helper}
            </Typography>

            {/* TextField */}
            <TextField 
              name={field.name}
              multiline 
              rows={4}
              fullWidth 
              value={formData[field.name] || ''} 
              onChange={handleChange} 
              disabled={!canEdit || isSaving}
              placeholder={`Digite aqui...`}
              sx={{
                flexGrow: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  backgroundColor: '#f8fafc',
                  fontSize: '0.875rem',
                  lineHeight: 1.6,
                  transition: 'all 0.2s ease',
                  height: '100%',
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                    borderWidth: '2px'
                  },
                  '&:hover': {
                    backgroundColor: '#ffffff',
                    '& fieldset': {
                      borderColor: alpha(field.color, 0.5)
                    }
                  },
                  '&.Mui-focused': {
                    backgroundColor: '#ffffff',
                    '& fieldset': {
                      borderColor: field.color,
                      borderWidth: '2px'
                    }
                  }
                }
              }}
            />
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>

  {/* Save Button */}
  {canEdit && (
    <Box sx={{ 
      mt: 4, 
      display: 'flex', 
      justifyContent: 'center',
      pb: 2
    }}>
      <LoadingButton 
        type="submit" 
        variant="contained" 
        size="large"
        loading={isSaving}
        startIcon={<SaveIcon />}
        sx={{
          px: 8,
          py: 1.8,
          fontSize: '1.05rem',
          fontWeight: 700,
          borderRadius: '12px',
          textTransform: 'none',
          backgroundColor: '#FF834F',
          boxShadow: '0 8px 20px rgba(255, 131, 79, 0.3)',
          '&:hover': {
            backgroundColor: '#e67342',
            boxShadow: '0 12px 28px rgba(255, 131, 79, 0.4)',
            transform: 'translateY(-2px)'
          },
          '&:disabled': {
            backgroundColor: '#cbd5e1',
            color: '#94a3b8'
          },
          transition: 'all 0.3s ease'
        }}
      >
        {isSaving ? 'A guardar...' : 'Salvar Estratégia'}
      </LoadingButton>
    </Box>
  )}
</Box>



      </Container>
      
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity} 
          variant="filled"
          sx={{ 
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
            fontSize: '0.95rem',
            fontWeight: 600
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StrategyPage;
